import React, { useState, useMemo } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const MyDatePicker = ({
  value,
  fullWidth = true,
  disabled = false,
  size = "small",
  format = "DD/MM/YYYY",
  onChange,
  required = false,
  label = "Select Date",
  min,
  max,
  ...otherProps
}) => {
  // Handle controlled/uncontrolled component
  const [internalValue, setInternalValue] = useState(() => {
    // Initialize with parsed value if provided and valid
    const parsedValue = dayjs(value);
    return parsedValue.isValid() ? parsedValue : null;
  });

  // Memoize parsed min and max dates to avoid unnecessary recalculations
  const minDate = useMemo(() => (min ? dayjs(min) : undefined), [min]);
  const maxDate = useMemo(() => (max ? dayjs(max) : undefined), [max]);

  // Determine the value to use: controlled (valid external value) or uncontrolled (internal)
  const parsedValue = useMemo(() => {
    if (value !== undefined) {
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed : null;
    }
    return internalValue;
  }, [value, internalValue]);

  const handleChange = (newValue) => {
    if (onChange) {
      onChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        disabled={disabled}
        slotProps={{
          // Merge user's slotProps with component's defaults
          ...otherProps.slotProps,
          textField: {
            // User's textField props
            ...otherProps.slotProps?.textField,
            // Component's defaults (override user's)
            fullWidth,
            size,
            required,
            // Merge sx styles
            sx: {
              minWidth: "100px",
              ...otherProps.slotProps?.textField?.sx,
            },
          },
        }}
        label={label}
        format={format}
        value={parsedValue}
        onChange={handleChange}
        minDate={minDate}
        maxDate={maxDate}
        {...otherProps}
      />
    </LocalizationProvider>
  );
};

export default MyDatePicker;
